import { createTheme } from '@mui/material/styles';

// Design System - Consistent Colors, Typography, and Spacing
const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#2563eb',        // Vibrant professional blue - trustworthy and modern
      light: '#3b82f6',       // Lighter blue
      dark: '#1e40af',        // Darker blue
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#f59e0b',        // Warm amber/orange - energetic and attention-grabbing
      light: '#fbbf24',      // Lighter amber
      dark: '#d97706',       // Darker amber
      contrastText: '#ffffff',
    },
    error: {
      main: '#ef4444',        // Modern red
      light: '#f87171',
      dark: '#dc2626',
    },
    warning: {
      main: '#f59e0b',        // Amber
      light: '#fbbf24',
      dark: '#d97706',
    },
    info: {
      main: '#3b82f6',        // Blue
      light: '#60a5fa',
      dark: '#2563eb',
    },
    success: {
      main: '#10b981',        // Modern green
      light: '#34d399',
      dark: '#059669',
    },
    background: {
      default: '#f8fafc',      // Soft, clean gray background
      paper: '#ffffff',         // Pure white for cards/paper
    },
    text: {
      primary: '#1e293b',       // Dark slate for primary text
      secondary: '#64748b',    // Medium slate for secondary text
    },
    divider: 'rgba(0, 0, 0, 0.08)',
    // Additional colors for gradients and special use cases
    gradients: {
      primary: 'linear-gradient(135deg, #2563eb 0%, #1e40af 100%)',
      secondary: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
      hero: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',  // Beautiful blue to purple
      background: 'linear-gradient(180deg, #f8fafc 0%, #ffffff 50%, #f1f5f9 100%)',
      light: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
      accent: 'linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%)',  // Warm amber gradient
      success: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
    },
    // Dark theme colors for sidebar/appbar
    dark: {
      main: '#0f172a',        // Deep slate
      light: '#1e293b',       // Slate
      lighter: '#334155',      // Lighter slate
      text: '#f1f5f9',        // Light slate text
      divider: '#475569',     // Medium slate divider
    },
  },
  typography: {
    fontFamily: [
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Roboto',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif',
    ].join(','),
    h1: {
      fontSize: '2.5rem',      // 40px
      fontWeight: 700,
      lineHeight: 1.2,
      letterSpacing: '-0.01562em',
    },
    h2: {
      fontSize: '2rem',         // 32px
      fontWeight: 700,
      lineHeight: 1.2,
      letterSpacing: '-0.00833em',
    },
    h3: {
      fontSize: '1.75rem',      // 28px
      fontWeight: 600,
      lineHeight: 1.3,
      letterSpacing: '0em',
    },
    h4: {
      fontSize: '1.5rem',       // 24px
      fontWeight: 600,
      lineHeight: 1.4,
      letterSpacing: '0.00735em',
    },
    h5: {
      fontSize: '1.25rem',      // 20px
      fontWeight: 600,
      lineHeight: 1.5,
      letterSpacing: '0em',
    },
    h6: {
      fontSize: '1.125rem',     // 18px
      fontWeight: 600,
      lineHeight: 1.5,
      letterSpacing: '0.0075em',
    },
    subtitle1: {
      fontSize: '1rem',         // 16px
      fontWeight: 500,
      lineHeight: 1.5,
      letterSpacing: '0.00938em',
    },
    subtitle2: {
      fontSize: '0.875rem',    // 14px
      fontWeight: 500,
      lineHeight: 1.57,
      letterSpacing: '0.00714em',
    },
    body1: {
      fontSize: '1rem',         // 16px
      fontWeight: 400,
      lineHeight: 1.5,
      letterSpacing: '0.00938em',
    },
    body2: {
      fontSize: '0.875rem',     // 14px
      fontWeight: 400,
      lineHeight: 1.43,
      letterSpacing: '0.01071em',
    },
    button: {
      fontSize: '0.9375rem',    // 15px
      fontWeight: 600,
      lineHeight: 1.75,
      letterSpacing: '0.02857em',
      textTransform: 'none',    // Don't auto-uppercase buttons
    },
    caption: {
      fontSize: '0.75rem',      // 12px
      fontWeight: 400,
      lineHeight: 1.66,
      letterSpacing: '0.03333em',
    },
    overline: {
      fontSize: '0.75rem',      // 12px
      fontWeight: 400,
      lineHeight: 2.66,
      letterSpacing: '0.08333em',
      textTransform: 'uppercase',
    },
  },
  spacing: 8, // Base spacing unit (8px)
  shape: {
    borderRadius: 8,            // Default border radius
  },
  shadows: [
    'none',
    '0px 2px 4px rgba(0,0,0,0.1)',
    '0px 4px 8px rgba(0,0,0,0.1)',
    '0px 8px 16px rgba(0,0,0,0.1)',
    '0px 12px 24px rgba(0,0,0,0.1)',
    '0px 16px 32px rgba(0,0,0,0.1)',
    '0px 20px 40px rgba(0,0,0,0.1)',
    '0px 24px 48px rgba(0,0,0,0.1)',
    '0px 28px 56px rgba(0,0,0,0.1)',
    '0px 32px 64px rgba(0,0,0,0.1)',
    '0px 36px 72px rgba(0,0,0,0.1)',
    '0px 40px 80px rgba(0,0,0,0.1)',
    '0px 44px 88px rgba(0,0,0,0.1)',
    '0px 48px 96px rgba(0,0,0,0.1)',
    '0px 52px 104px rgba(0,0,0,0.1)',
    '0px 56px 112px rgba(0,0,0,0.1)',
    '0px 60px 120px rgba(0,0,0,0.1)',
    '0px 64px 128px rgba(0,0,0,0.1)',
    '0px 68px 136px rgba(0,0,0,0.1)',
    '0px 72px 144px rgba(0,0,0,0.1)',
    '0px 76px 152px rgba(0,0,0,0.1)',
    '0px 80px 160px rgba(0,0,0,0.1)',
    '0px 84px 168px rgba(0,0,0,0.1)',
    '0px 88px 176px rgba(0,0,0,0.1)',
    // MUI Dialog / Paper default elevation is 24 — must exist (indices 0..24 = 25 entries)
    '0px 92px 184px rgba(0,0,0,0.12)',
  ],
  components: {
    // Mobile tap feedback: 100–150ms, scale/elevation only (no hover on touch)
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          padding: '10px 24px',
          fontSize: '0.9375rem',
          fontWeight: 600,
          textTransform: 'none',
          boxShadow: 'none',
          transition: 'transform 0.12s ease-out, box-shadow 0.12s ease-out',
          '@media (hover: none)': {
            '&:hover': { boxShadow: 'none', transform: 'none' },
            '&:active': {
              transform: 'scale(0.98)',
              boxShadow: '0 1px 2px rgba(0,0,0,0.08)',
            },
          },
          '@media (hover: hover)': {
            '&:hover': { boxShadow: '0px 4px 8px rgba(0,0,0,0.15)' },
          },
        },
        contained: {
          '@media (hover: none)': {
            '&:hover': { boxShadow: 'none' },
            '&:active': {
              transform: 'scale(0.98)',
              boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
            },
          },
          '@media (hover: hover)': {
            '&:hover': { boxShadow: '0px 4px 8px rgba(0,0,0,0.15)' },
          },
        },
        sizeLarge: { padding: '12px 32px', fontSize: '1rem' },
        sizeSmall: { padding: '6px 16px', fontSize: '0.875rem' },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          transition: 'transform 0.12s ease-out, box-shadow 0.12s ease-out',
          '@media (hover: none)': {
            '&:hover': { backgroundColor: 'transparent' },
            '&:active': {
              transform: 'scale(0.92)',
              boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
            },
          },
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          transition: 'transform 0.12s ease-out, box-shadow 0.12s ease-out',
          '@media (hover: none)': {
            '&:hover': { backgroundColor: 'transparent' },
            '&:active': {
              transform: 'scale(0.995)',
              boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.04)',
            },
          },
        },
      },
    },
    MuiListItem: {
      styleOverrides: {
        root: {
          transition: 'transform 0.12s ease-out',
          '@media (hover: none)': {
            '&:active': { transform: 'scale(0.998)' },
          },
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          transition: 'transform 0.12s ease-out',
          '@media (hover: none)': {
            '&:active': { transform: 'scale(0.99)' },
          },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        select: {
          transition: 'transform 0.12s ease-out',
          '@media (hover: none)': {
            '&:active': { transform: 'scale(0.99)' },
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          transition: 'transform 0.12s ease-out',
          '@media (hover: none)': {
            '&:active': { transform: 'scale(0.96)' },
          },
        },
      },
    },
    MuiFab: {
      styleOverrides: {
        root: {
          transition: 'transform 0.12s ease-out, box-shadow 0.12s ease-out',
          '@media (hover: none)': {
            '&:active': {
              transform: 'scale(0.95)',
              boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
            },
          },
        },
      },
    },
    MuiCardActionArea: {
      styleOverrides: {
        root: {
          transition: 'transform 0.12s ease-out',
          '@media (hover: none)': {
            '&:active': { transform: 'scale(0.99)' },
          },
        },
      },
    },
    MuiBottomNavigationAction: {
      styleOverrides: {
        root: {
          transition: 'transform 0.12s ease-out',
          '@media (hover: none)': {
            '&:active': { transform: 'scale(0.92)' },
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
          },
        },
      },
    },
    // Card overrides
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0px 4px 8px rgba(0,0,0,0.1)',
        },
      },
    },
    // Paper overrides
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
      },
    },
    // AppBar overrides
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: '0px 2px 4px rgba(0,0,0,0.1)',
        },
      },
    },
    // Typography overrides for consistent font weights
    MuiTypography: {
      styleOverrides: {
        root: {
          // Ensure consistent font rendering
          WebkitFontSmoothing: 'antialiased',
          MozOsxFontSmoothing: 'grayscale',
        },
      },
    },
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 960,
      lg: 1280,
      xl: 1920,
    },
  },
});

export default theme;

